import { useState, useEffect, useRef } from "react";
import { useParams, useLocation } from "wouter";
import { SuperAdminLayout } from "@/components/admin/SuperAdminLayout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { EnhancedModal } from "@/components/ui/EnhancedModal";
import { JobStatus, Job, ReferralStatus } from "@/lib/schema";
import { ReferralManager } from "@/components/referrals/ReferralManager";
import { type HireFormData } from "@/components/admin/HiredConfirmationModal";
import { AdminReferralWithUI, getAdminReferralsWithUI, subscribeToAdminReferralsWithUI } from "@/api/referrals";
import { getJob, updateJob } from "@/api/jobs";
import { formatDisplayText, formatRewardAmount } from "@/lib/textUtils";
import { JobForm } from "@/components/jobs/JobForm";
import { BackToPageButton } from "@/components/jobs/BackToPageButton";
import { JobKPICards } from "@/components/jobs/JobKPICards";
import { JobDetailsSkeleton } from "@/components/jobs/JobDetailsSkeleton";
import { JobDetailsTabs } from "@/components/jobs/JobDetailsTabs";
import { PauseJobModal } from "@/components/jobs/PauseJobModal";
import { ActivateJobModal } from "@/components/jobs/ActivateJobModal";
import iconTotalReferrals from "@/assets/dashboard/icon_totalreferrals.svg";
import iconRewardsEarned from "@/assets/dashboard/icon_rewardsearned.svg";
import { getStatusColor, getPriorityColor, transformJobForDisplay } from "@/lib/jobUtils";
import { 
  JobFormState,
  getInitialJobFormState,
  jobToFormState,
  formStateToJobData
} from "@/lib/jobFormUtils";
import { 
  calculateJobAnalytics, 
  handleJobStatusUpdate,
  navigateToJobs
} from "@/lib/jobDetailsUtils";
import { useAuth } from "@/hooks/useAuth";
import { 
  Edit,
  Pause,
  Play,
  Target
} from "lucide-react";

export default function JobDetails() {
  const { id } = useParams();
  const [, navigate] = useLocation();
  const { userData } = useAuth();
  const [showEditModal, setShowEditModal] = useState(false);
  const [jobForm, setJobForm] = useState<JobFormState>(getInitialJobFormState());
  
  // Job data state
  const [job, setJob] = useState<Job | null>(null);
  const [referrals, setReferrals] = useState<AdminReferralWithUI[]>([]);
  const [loading, setLoading] = useState(true);
  const [referralsLoading, setReferralsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [showPauseDialog, setShowPauseDialog] = useState(false);
  const [showActivateDialog, setShowActivateDialog] = useState(false);
  
  // Referral management state
  const [selectedReferral, setSelectedReferral] = useState<AdminReferralWithUI | null>(null);
  const [showReferralViewer, setShowReferralViewer] = useState(false);
  
  // Ref to access current selectedReferral value in subscription callback
  const selectedReferralRef = useRef<AdminReferralWithUI | null>(null);
  
  // Keep ref in sync with state
  useEffect(() => {
    selectedReferralRef.current = selectedReferral;
  }, [selectedReferral]);

  // Fetch job data function
  const fetchJobData = async () => {
    if (!id) {
      setError("No job ID provided");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const jobData = await getJob(id);
      
      if (!jobData) {
        setError("Job not found");
        setLoading(false);
        return;
      }
      
      setJob(jobData);
    } catch (err) {
      console.error("Error fetching job:", err);
      setError("Failed to load job details");
    } finally {
      setLoading(false);
    }
  };

  // Fetch job data on mount and when id changes
  useEffect(() => {
    fetchJobData();
  }, [id]);

  // Set up real-time subscriptions for referrals
  useEffect(() => {
    if (!id || !job) return;

    let unsubscribeReferrals: (() => void) | undefined;
    let isMounted = true; // Track if component is still mounted

    const setupReferralsSubscription = async () => {
      try {
        setReferralsLoading(true);
        
        // Load initial referrals data
        const initialReferrals = await getAdminReferralsWithUI({ jobId: id });
        
        // Only update state if component is still mounted
        if (!isMounted) return;
        
        setReferrals(initialReferrals);
        
        // Set up real-time subscription for this job's referrals
        unsubscribeReferrals = subscribeToAdminReferralsWithUI(
          (newReferrals) => {
            // Only update state if component is still mounted
            if (!isMounted) return;
            
            setReferrals(newReferrals);
            
            // Update selectedReferral if it's in the new data
            // Use ref to get current value and avoid stale closure
            const currentSelectedReferral = selectedReferralRef.current;
            if (currentSelectedReferral) {
              const updatedSelectedReferral = newReferrals.find(r => r.id === currentSelectedReferral.id);
              if (updatedSelectedReferral) {
                setSelectedReferral(updatedSelectedReferral);
              }
            }
          },
          { jobId: id }
        );
        
      } catch (err) {
        console.error("Error setting up referrals subscription:", err);
        // Don't set error state for referrals, just log and continue
      } finally {
        // Only update loading state if component is still mounted
        if (isMounted) {
          setReferralsLoading(false);
        }
      }
    };

    setupReferralsSubscription();

    // Cleanup subscription on unmount or when dependencies change
    return () => {
      isMounted = false; // Mark as unmounted
      if (unsubscribeReferrals) {
        unsubscribeReferrals();
      }
    };
  }, [id, job]); // Only re-subscribe when job ID or job data changes


  // Calculate analytics using extracted utility
  const analytics = job ? calculateJobAnalytics(job, referrals) : null;

  const handleStatusUpdate = async (newStatus: JobStatus | string) => {
    if (!job || updatingStatus) return;
    
    try {
      setUpdatingStatus(true);
      const updatedJob = await handleJobStatusUpdate(job.id, newStatus);
      if (updatedJob) {
        setJob(updatedJob);
        // TODO: Add toast notification for successful update
      }
    } catch (error) {
      console.error('Failed to update job status:', error);
      // TODO: Add error toast notification
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleEditJob = () => {
    if (!job) return;
    setJobForm(jobToFormState(job));
    setShowEditModal(true);
  };

  const handleSaveEditJob = async (formData: JobFormState, recruiters?: Array<{id: string, firstName: string, lastName: string}>): Promise<boolean> => {
    if (!job) return false;
    
    try {
      const updates = formStateToJobData(formData, recruiters);
      const updatedJob = await updateJob(job.id, updates);
      
      if (updatedJob) {
        setJob(updatedJob);
        setShowEditModal(false);
        setJobForm(getInitialJobFormState());
        // TODO: Add toast notification for successful update
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Failed to update job:', error);
      // TODO: Add error toast notification
      return false;
    }
  };

  const handlePauseJobClick = () => {
    setShowPauseDialog(true);
  };

  const handleConfirmPause = async () => {
    setShowPauseDialog(false);
    await handleStatusUpdate(JobStatus.PAUSED);
  };

  const handleActivateJobClick = () => {
    setShowActivateDialog(true);
  };

  const handleConfirmActivate = async () => {
    setShowActivateDialog(false);
    await handleStatusUpdate(JobStatus.ACTIVE);
  };

  // Referral management handlers
  const handleViewReferral = (referral: AdminReferralWithUI) => {
    setSelectedReferral(referral);
    setShowReferralViewer(true);
  };

  const handleStatusChangeSuccess = (referralId: string, newStatus: ReferralStatus, hireData?: HireFormData) => {
    console.log(`✅ Successfully updated referral ${referralId} status to ${newStatus}`, hireData ? { hireData } : '');
    
    // Optimistic update - update UI instantly
    setReferrals(prev => prev.map(ref => 
      ref.id === referralId 
        ? { ...ref, status: newStatus, updatedAt: new Date() }
        : ref
    ));
    
    // Update the selected referral if it's the one being updated
    if (selectedReferral && selectedReferral.id === referralId) {
      setSelectedReferral(prev => prev ? { ...prev, status: newStatus, updatedAt: new Date() } : prev);
    }
  };

  const handleNoteAddSuccess = (referralId: string, note: string) => {
    // Optimistic update for better UX - add the note immediately to selectedReferral
    if (selectedReferral && selectedReferral.id === referralId && userData) {
      const newNote = {
        note: note,
        date: new Date(),
        recruiterId: userData.id
      };
      
      setSelectedReferral(prev => prev ? {
        ...prev,
        recruiterNote: [...(prev.recruiterNote || []), newNote],
        updatedAt: new Date()
      } : prev);
    }
    
    // Real-time subscription will sync with server data shortly after
  };

  const handleError = (error: string) => {
    console.error('Referral management error:', error);
    // In a real app, you might want to show a toast or update error state
  };


  if (loading) {
    return (
      <SuperAdminLayout title="Job Details" subtitle="Loading job information...">
        <JobDetailsSkeleton />
      </SuperAdminLayout>
    );
  }

  if (error || !job) {
    return (
      <SuperAdminLayout title="Job Details" subtitle={error || "Job not found"}>
        <div className="flex flex-col items-center justify-center min-h-[400px]">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {error || "Job not found"}
            </h2>
            <p className="text-gray-600 mb-4">
              {error === "Job not found" 
                ? "The job you're looking for doesn't exist or has been removed."
                : "There was an error loading the job details."
              }
            </p>
            <div className="flex gap-3">
              <BackToPageButton 
                onClick={() => navigateToJobs(navigate)}
                variant="outline"
              />
              {error && error !== "Job not found" && (
                <Button
                  onClick={fetchJobData}
                >
                  Try Again
                </Button>
              )}
            </div>
          </div>
        </div>
      </SuperAdminLayout>
    );
  }

  return (
    <SuperAdminLayout 
      title={job.title}
      subtitle={`${job.location} • ${formatDisplayText(job.category)}`}
      headerClassName="mb-6 lg:mb-8 xl:mb-10"
      contentClassName="p-4 lg:p-8 xl:p-10 pt-0"
      maxWidth="7xl"
    >
      {/* Header Actions */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-6 lg:mb-8 xl:mb-10 gap-4 lg:gap-6">
        <div className="flex items-center gap-4">
          <BackToPageButton onClick={() => navigateToJobs(navigate)} />
        </div>
        
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 lg:gap-4">
          <div className="flex items-center gap-2 lg:gap-3">
            <Badge className={`${getStatusColor(job.status)} border-0 font-medium px-3 py-1`}>
              {job.status.charAt(0).toUpperCase() + job.status.slice(1)}
            </Badge>
            <Badge className={`${getPriorityColor(job.priority)} border-0 font-medium px-3 py-1`}>
              {job.priority.charAt(0).toUpperCase() + job.priority.slice(1)} Priority
            </Badge>
          </div>
          <div className="flex items-center gap-2 lg:gap-3">
            {job.status === JobStatus.ACTIVE && (
              <Button
                variant="secondary"
                size="sm"
                onClick={handlePauseJobClick}
                disabled={updatingStatus}
                className="font-medium lg:px-4"
                data-testid="button-pause-job"
              >
                <Pause className="h-4 w-4 mr-2" />
                {updatingStatus ? 'Pausing...' : 'Pause'}
              </Button>
            )}
            {job.status === JobStatus.PAUSED && (
              <Button
                variant="secondary"
                size="sm"
                onClick={handleActivateJobClick}
                disabled={updatingStatus}
                className="font-medium lg:px-4"
                data-testid="button-activate-job"
              >
                <Play className="h-4 w-4 mr-2" />
                {updatingStatus ? 'Activating...' : 'Activate'}
              </Button>
            )}
            {job.status !== 'filled' && (
              <Button
                variant="secondary"
                size="sm"
                onClick={handleEditJob}
                className="font-medium lg:px-4"
                data-testid="button-edit-job"
              >
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="mb-8 lg:mb-10 xl:mb-12">
        <JobKPICards 
          jobs={[]} 
          loading={false}
          customKPIs={[
            {
              title: "Total Referrals",
              value: (analytics?.totalReferrals || 0).toString(),
              icon: iconTotalReferrals,
            },
            {
              title: "AirPoints Reward",
              value: job.rewardInAirPoints ? `R${formatRewardAmount(job.rewardInAirPoints)}` : 'N/A',
              icon: iconRewardsEarned,
            },
              {
                title: "Avg Match Score",
                value: `${analytics?.averageMatchScore || 0}%`,
                icon: Target,
                variant: "dark" as const,
              }
          ]}
        />
      </div>

      {/* Main Content Tabs */}
      <div className="w-full">
        <JobDetailsTabs 
          job={job}
          referrals={referrals}
          referralsLoading={referralsLoading}
          onViewReferral={handleViewReferral}
        />
      </div>

          {/* Edit Job Modal */}
          <EnhancedModal
            open={showEditModal}
            onOpenChange={setShowEditModal}
            title="Edit Job"
            description="Update the job information and settings below."
            maxWidth="4xl"
            onClose={() => setShowEditModal(false)}
          >
            {/* Header */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-slate-800 mb-2">Edit Job</h2>
              <p className="text-slate-600">Update the job information and settings below.</p>
            </div>

            {/* Job Form */}
            <JobForm
              initialData={jobForm}
              onSubmit={handleSaveEditJob}
              onCancel={() => setShowEditModal(false)}
              submitButtonText="Save Changes"
              showHeader={false}
            />
          </EnhancedModal>

          {/* Pause Job Modal */}
          <PauseJobModal
            open={showPauseDialog}
            onOpenChange={setShowPauseDialog}
            job={job ? transformJobForDisplay(job) : null}
            onConfirm={handleConfirmPause}
          />

          {/* Activate Job Modal */}
          <ActivateJobModal
            open={showActivateDialog}
            onOpenChange={setShowActivateDialog}
            job={job ? transformJobForDisplay(job) : null}
            onConfirm={handleConfirmActivate}
          />

          {/* Referral Manager - handles all referral modals */}
          <ReferralManager
            selectedReferral={selectedReferral}
            isReferralViewerOpen={showReferralViewer}
            onCloseReferralViewer={() => setShowReferralViewer(false)}
            onStatusChangeSuccess={handleStatusChangeSuccess}
            onNoteAddSuccess={handleNoteAddSuccess}
            onError={handleError}
            jobId={id}
          />
    </SuperAdminLayout>
  );
}